import React, { useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { CheckCircle2, MessageSquare, ShieldCheck, FileText, Lock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SMS_CONSENT_TEXT =
  'I agree to receive SMS messages from NexFlow Plumbing & Drain Services regarding ' +
  'appointment reminders, dispatch notifications, account updates, verification codes, ' +
  'and customer service communications. Message frequency varies. Message and data ' +
  'rates may apply. Reply STOP to opt out. Reply HELP for assistance. Consent is not ' +
  'a condition of purchase.';

const SmsOptIn = () => {
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!consent) { setError('Please check the consent box above before submitting.'); return; }
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/public/sms-consent`, {
        ...form,
        consent: true,
        consent_source: 'sms_opt_in_page',
      });
      setDone(data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not record consent — please try again.');
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      {/* Header */}
      <div className="border-b border-[#262626]">
        <div className="max-w-3xl mx-auto px-5 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="font-['Outfit'] text-white font-semibold tracking-wide">NEXFLOW PLUMBING</span>
          </Link>
          <Link to="/" className="text-[#A3A3A3] text-sm hover:text-white">Home</Link>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10 space-y-10">
        <div>
          <p className="text-[#D4AF37] text-xs font-bold tracking-[0.3em] mb-2">SMS COMMUNICATIONS</p>
          <h1 className="font-['Outfit'] text-3xl sm:text-4xl font-bold mb-3">SMS Opt-In & Consent</h1>
          <p className="text-[#A3A3A3] text-base">
            Use the form below to give NexFlow Inc. permission to send you text messages about your service appointments,
            verification codes, payment reminders, and membership updates. Your information is stored securely and is
            never sold or shared with third parties.
          </p>
        </div>

        {/* What we'll send */}
        <div className="grid sm:grid-cols-2 gap-3 text-sm">
          {[
            { i: MessageSquare, t: 'Appointment reminders & schedule updates' },
            { i: ShieldCheck,   t: 'Verification codes for sign-in & account security' },
            { i: CheckCircle2,  t: 'Service status & on-the-way notifications' },
            { i: FileText,      t: 'Payment & membership renewal reminders' },
          ].map(({ i: Icon, t }, idx) => (
            <div key={idx} className="bg-[#141414] border border-[#262626] rounded-md p-3 flex items-start gap-2">
              <Icon className="w-4 h-4 text-[#D4AF37] mt-0.5 shrink-0" />
              <p className="text-white text-sm">{t}</p>
            </div>
          ))}
        </div>

        {done ? (
          <div data-testid="sms-opt-in-success" className="bg-[#141414] border border-[#D4AF37]/40 rounded-md p-6 text-center space-y-3">
            <CheckCircle2 className="w-10 h-10 text-[#D4AF37] mx-auto" />
            <p className="text-white text-lg font-medium">Thanks — your consent is recorded.</p>
            <p className="text-[#A3A3A3] text-sm">Recorded {new Date(done.consent_at).toLocaleString()}.</p>
            <p className="text-[#A3A3A3] text-xs">Reply <span className="text-[#D4AF37]">STOP</span> at any time to unsubscribe, or <span className="text-[#D4AF37]">HELP</span> for assistance.</p>
            <Link to="/" className="inline-block text-[#D4AF37] text-sm hover:text-[#F3E5AB]">← Back to NexFlow Plumbing</Link>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-[#141414] border border-[#262626] rounded-md p-6 space-y-5">
            {error && <div data-testid="sms-opt-in-error" className="bg-[#7F1D1D] text-white p-3 rounded-md text-sm">{error}</div>}

            <div>
              <Label className="text-white">Full Name *</Label>
              <Input
                data-testid="sms-opt-in-name"
                required
                value={form.full_name}
                onChange={(e) => setForm({ ...form, full_name: e.target.value })}
                className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                placeholder="Jane Doe"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label className="text-white">Email *</Label>
                <Input
                  data-testid="sms-opt-in-email"
                  type="email"
                  required
                  inputMode="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                  placeholder="you@example.com"
                />
              </div>
              <div>
                <Label className="text-white">Cell Phone *</Label>
                <Input
                  data-testid="sms-opt-in-phone"
                  type="tel"
                  required
                  inputMode="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="bg-[#0A0A0A] border-[#262626] text-white h-11 text-base"
                  placeholder="(555) 555-5555"
                />
                <p className="text-[#525252] text-xs mt-1">US 10-digit or E.164 format. Carrier messaging rates apply.</p>
              </div>
            </div>

            {/* Consent checkbox */}
            <label className="flex items-start gap-3 p-3 rounded-md border border-[#262626] hover:border-[#D4AF37]/50 transition-colors cursor-pointer">
              <input
                data-testid="sms-opt-in-checkbox"
                type="checkbox"
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 accent-[#D4AF37] w-4 h-4"
              />
              <span className="text-[#A3A3A3] text-sm leading-relaxed">{SMS_CONSENT_TEXT}</span>
            </label>

            <Button
              data-testid="sms-opt-in-submit"
              type="submit"
              disabled={submitting}
              className="w-full h-12 bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] disabled:opacity-40 text-base font-medium"
            >
              {submitting ? 'Submitting…' : 'Confirm Opt-In'}
            </Button>
            <p className="text-center text-[#525252] text-xs">
              By submitting, you confirm the number above belongs to you and you are authorized to receive SMS at this number.
            </p>
          </form>
        )}

        {/* STOP / HELP block */}
        <div className="bg-[#141414] border border-[#262626] rounded-md p-5 space-y-2">
          <p className="text-white font-medium flex items-center gap-2"><Lock className="w-4 h-4 text-[#D4AF37]" /> Quick reference</p>
          <ul className="text-[#A3A3A3] text-sm space-y-1 list-disc pl-5">
            <li>Reply <span className="text-[#D4AF37] font-mono">STOP</span> to unsubscribe at any time.</li>
            <li>Reply <span className="text-[#D4AF37] font-mono">HELP</span> for assistance.</li>
            <li>Message frequency varies based on your active appointments and account activity.</li>
            <li>Message and data rates may apply.</li>
            <li>Supported carriers include AT&T, Verizon, T-Mobile, and most US carriers.</li>
            <li>We will never sell, rent, or share your phone number with third parties.</li>
          </ul>
        </div>

        {/* Footer links */}
        <div className="border-t border-[#262626] pt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
          <Link to="/terms" data-testid="terms-link" className="text-[#A3A3A3] hover:text-white">Terms of Service</Link>
          <Link to="/privacy" data-testid="privacy-link" className="text-[#A3A3A3] hover:text-white">Privacy Policy</Link>
          <Link to="/sms-opt-in" className="text-[#A3A3A3] hover:text-white">SMS Opt-In</Link>
          <a href="mailto:support@nexflowplumbing.com" className="text-[#A3A3A3] hover:text-white">support@nexflowplumbing.com</a>
        </div>
      </div>
    </div>
  );
};

export default SmsOptIn;
